import type { ExperimentsListResponse, ExperimentDetailResponse } from "@/types";

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";
import type { ExperimentStatus } from "@/utils/experiments";

// Shared params atom for experiments list
export type ExperimentsListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  refresh?: number;
};

export const experimentsListParamsAtom = atom<ExperimentsListParams>({
  ...createPaginationParams(),
  filters: {},
});

// Hydration flag to delay initial query until URL/filters are synced
export const experimentsHydratedAtom = atom<boolean>(false);

// GET /experiments list query
export const experimentsQueryAtom = atomWithQuery<ExperimentsListResponse>((get) => {
  const params = get(experimentsListParamsAtom);
  const hydrated = get(experimentsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["experiments", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page },
        params.filters || {}
      );

      const { data } = await apiClient.get("/experiments", { params: apiParams });

      return data as ExperimentsListResponse;
    },
    enabled: hydrated
  });
});

// Payload for set-status API
export type SetExperimentStatusPayload = {
  id: number;
  status: ExperimentStatus;
  blocking_reason?: string;
  cancellation_reason?: string;
};

// Update experiment status atom
export const setExperimentStatus = atom(
  null,
  async (get, set, payload: SetExperimentStatusPayload) => {
    const { id, status, blocking_reason, cancellation_reason } = payload;
    const body: Record<string, string> = { status };
    if (blocking_reason !== undefined) body.blocking_reason = blocking_reason;
    if (cancellation_reason !== undefined) body.cancellation_reason = cancellation_reason;

    await apiClient.put(`/experiments/${id}/set-status`, body);

    // Trigger refresh by updating params
    const currentParams = get(experimentsListParamsAtom);
    set(experimentsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Update experiment configuration atom
export const setExperimentConfig = atom(
  null,
  async (
    get,
    set,
    {
      id,
      config
    }: {
      id: number;
      config: {
        trackers: string[];
        tracker_keys: string[];
        rules: {
          click: Array<{ name: string; css_selector: string }>;
          impression: Array<{ name: string; css_selector: string }>;
        };
        stable_attributes: string[];
        cookies: string[];
        campaign_params: string[];
      };
    }
  ) => {
    await apiClient.put(`/experiments/${id}/set-config`, { config });

    // Trigger refresh by updating params
    const currentParams = get(experimentsListParamsAtom);
    set(experimentsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Update experiment extra settings atom
export const setExperimentExtra = atom(
  null,
  async (
    get,
    set,
    {
      id,
      extra
    }: {
      id: number;
      extra: {
        force_click_tracking: boolean;
        capture_all_properties: boolean;
        referrer_tracking: boolean;
      };
    }
  ) => {
    const payload = {
      extra: {
        force_click_tracking: extra.force_click_tracking,
        capture_all_properties: extra.capture_all_properties,
        referrer_tracking: extra.referrer_tracking
      }
    };

    await apiClient.put(`/experiments/${id}/set-extra`, payload);

    // Trigger refresh by updating params
    const currentParams = get(experimentsListParamsAtom);
    set(experimentsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Experiment detail atoms
export const experimentDetailIdAtom = atom<number | null>(null);
export const experimentDetailRefreshAtom = atom<number>(0);

export const experimentDetailQueryAtom = atomWithQuery<ExperimentDetailResponse>((get) => {
  const experimentId = get(experimentDetailIdAtom);
  const refresh = get(experimentDetailRefreshAtom);

  return createTableQueryOptions({
    queryKey: ["experiment", experimentId, refresh] as const,
    queryFn: async () => {
      if (!experimentId) {
        throw new Error("Experiment ID is required");
      }
      const { data } = await apiClient.get(`/experiments/${experimentId}`);
      return data as ExperimentDetailResponse;
    },
    enabled: Boolean(experimentId)
  });
});

